import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Switch, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControlLabel, Snackbar, Alert
} from '@mui/material';
import api from '../api/api';

const ListasPrecioGestion = () => {
  const [listas, setListas] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ nombre_lista: '', es_lista_default: false });
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success' });



  const fetchListas = async () => {
    try {
      const res = await api.get('/listas-precio/?solo_activas=false');
      setListas(res.data || []);
    } catch {
      setSnackbar({ open: true, msg: 'Error al consultar listas', severity: 'error' });
    }
  };

  useEffect(() => { fetchListas(); }, []);

  const handleOpenCreate = () => {
    setEditingId(null);
    setFormData({ nombre_lista: '', es_lista_default: false, habilitado: true });
    setModalOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditingId(row.id_lista_precio);
    setFormData({
      nombre_lista: row.nombre_lista,
      es_lista_default: row.es_lista_default,
      habilitado: row.habilitado
    });
    setModalOpen(true);
  };

  const handleToggleHabilitado = async (row) => {
    try {
      await api.put(`/listas-precio/${row.id_lista_precio}`, {
        habilitado: !row.habilitado
      });
      setSnackbar({
        open: true,
        msg: `Lista ${!row.habilitado ? 'habilitada' : 'deshabilitada'} correctamente`,
        severity: 'success'
      });
      fetchListas();
    } catch (err) {
      setSnackbar({ open: true, msg: 'Error al cambiar estado', severity: 'error' });
    }
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await api.put(`/listas-precio/${editingId}`, formData);
        setSnackbar({ open: true, msg: 'Lista de precio actualizada', severity: 'success' });
      } else {
        await api.post('/listas-precio/', formData);
        setSnackbar({ open: true, msg: 'Lista de precio creada', severity: 'success' });
      }
      setModalOpen(false);
      fetchListas();
    } catch (err) {
      const detail = err.response?.data?.detail;
      let errorMsg = 'Error al guardar';

      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(d => `${d.loc?.[1] || 'Campo'}: ${d.msg}`).join(', ');
      }

      setSnackbar({ open: true, msg: errorMsg, severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Listas de Precio</Typography>
        <Button variant="contained" onClick={handleOpenCreate}>Nueva Lista</Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nombre de Lista</TableCell>
              <TableCell align="center">Lista Predeterminada</TableCell>
              <TableCell align="center">Habilitado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {listas.map((row) => (
              <TableRow key={row.id_lista_precio}>
                <TableCell>{row.id_lista_precio}</TableCell>
                <TableCell>{row.nombre_lista}</TableCell>
                <TableCell align="center">
                  <Switch checked={row.es_lista_default} disabled />
                </TableCell>
                <TableCell align="center">
                  <Switch 
                    checked={row.habilitado} 
                    onChange={() => handleToggleHabilitado(row)} 
                  />
                </TableCell>
                <TableCell align="center">
                  <Button size="small" variant="outlined" onClick={() => handleOpenEdit(row)}>
                    Editar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)}>
        <DialogTitle>{editingId ? 'Editar Lista de Precios' : 'Nueva Lista de Precios'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Nombre de la Lista" fullWidth
            value={formData.nombre_lista} onChange={(e) => setFormData({ ...formData, nombre_lista: e.target.value })}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.es_lista_default}
                onChange={(e) => setFormData({ ...formData, es_lista_default: e.target.checked })}
              />
            }
            label="Marcar como predeterminada"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.habilitado}
                onChange={(e) => setFormData({ ...formData, habilitado: e.target.checked })}
              />
            }
            label="Habilitado"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={!formData.nombre_lista.trim()}>
            {editingId ? 'Guardar Cambios' : 'Crear Lista'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ListasPrecioGestion;